import { _ } from "../../core/polyglot/polyglot.js";
import { getResult } from "../../core/helpers/functional-util.js";
import CollapsedActionsBlockWidget from "../../core/chrome/CollapsedActionsBlockWidget.js";
import document from "../../core/chrome/document.js";
import HelpWizard from "../../core/chrome/HelpWizard/HelpWizard.js";
import OldSpinnerWidget from "../../deprecated/OldSpinnerWidget/OldSpinnerWidget.js"; // eslint-disable-line import-x/no-deprecated -- todo: migrate to new spinner
import periodPicker from "../../core/widgets/periodPicker.js";
import session from "../../core/session.js";
import TitleWidget from "../../core/chrome/TitleWidget2.js";

/**
 * `accountingDocument` is the abstract base document for all
 * accounting documents.
 *
 * TODO: should be abstract, but since many documents just
 * instantiate it (function old style inheritance), we obviously
 * can't do it.
 */
export default document.subclass((that, my) => {
	/**
	 * `that.title` and `that.subtitle` can be any renderable
	 * objects.
	 */
	that.title = function () {};
	that.subtitle = function () {};
	that.headerTitle = undefined;

	/**
	 * Returns an array of literal help step definitions.
	 *
	 * Each step must define a `content` property holding a string or polyglot
	 * object, as well as the following optional fields:
	 *
	 * - title: Optional title of the help step. The value is a string or
	 * polyglot object.
	 *
	 * - element: Optional "data-help" attribute of a DOM element to be highlighted.
	 *
	 * - visible: Optional boolean to hide the help step.
	 *
	 * @example
	 * [
	 *   {
	 *     title: _("Step title"),
	 *     content: _("Step content"),
	 *     element: "my-element"
	 *   }
	 * ]
	 */
	that.help = function () {
		return [];
	};

	/**
	 * Returns an array of actions. Each action must be a
	 * renderable object.
	 *
	 * `actions` can also be a function returning an array, in which case it
	 * will be evaluated just before rendering the actions.
	 */
	// TODO: move to my.
	that.actions = [];

	/**
	 * Returns true if the actions menu should be visible only for
	 * mobile devices.
	 */
	that.isActionsMenuMobileOnly = function () {
		return false;
	};

	/**
	 * Returns true if the receiver has a actions menu. If false, no
	 * actions menu or menu toggler will be rendered.
	 */
	that.hasActionsMenu = function () {
		return true;
	};

	/**
	 * Returns true if a navigation menu should be displayed when
	 * setting the document.
	 */
	that.hasNavigation = function () {
		return true;
	};

	/**
	 * Returns true if a header menu should be displayed when
	 * setting the document.
	 */
	that.hasHeader = function () {
		return true;
	};

	/**
	 * Returns true if the help trigger button should be rendered
	 * as part of the document title.
	 */
	that.hasHelp = function () {
		return true;
	};

	/**
	 * Some actions changes the content of the period picker
	 * but since it is often cache with the document, it requires
	 * to be updated manually
	 */
	that.updatePeriodPicker = function () {
		my.periodPicker.updatePeriods();
	};

	/**
	 * Method called each time the global period in the URL is updated.
	 */
	that.periodChanged = function (fallback) {
		fallback();
	};

	/**
	 * Hook triggered whenever the actions widget is toggled.
	 */
	that.actionsVisibilityChanged = function () {};

	/**
	 * Hook triggered whenever the navigation widget icon mode is toggled.
	 */
	that.navigationIconModeToggled = function () {};

	my.titleWidget = (html) => {
		html.render(
			new TitleWidget({
				title: function (html) {
					html.render(that.title);
				},
				subtitle: function (html) {
					html.render(that.subtitle);
				},
				helpWizard: new HelpWizard({
					stepsBuilder: that.help,
				}),
				helpEnabled: that.hasHelp(),
			}),
		);
	};

	// eslint-disable-next-line import-x/no-deprecated -- todo: migrate to new spinner
	my.spinner = new OldSpinnerWidget({
		dark: true,
		size: 68,
	});

	my.periodPicker = periodPicker({
		activeYears: session.getCompany().getActiveYears(),
		redirectToPeriod: session.redirectToPeriod,
		period: session.getPeriod(),
	});

	that.renderContentOn = function (html) {
		html.render(my.titleWidget);
		html.div({ id: "document" }, my.renderDocumentContentOn);
	};

	that.renderMobileOnlyActionsOn = function (html) {
		if (!that.hasPeriodPicker()) {
			return;
		}
		html.label(_("Select period"));
		html.render(my.periodPicker);
	};

	that.renderTopActionsOn = function (html) {
		if (!that.hasPeriodPicker()) {
			return;
		}
		html.render(my.periodPicker);
	};

	my.renderUpdatingContentOn = function (html) {
		my.renderSpinnerOn(html);
	};

	my.renderSpinnerOn = function (html) {
		html.div(
			{ class: "row" },
			html.div(
				{
					id: "loading-progress",
					class: "col-sm-4 col-sm-offset-4 text-center",
				},
				my.spinner,
			),
		);
	};

	/**
	 * Default actions rendering method. Can be overridden.
	 */
	that.renderActionsOn = function (html) {
		html.render(my.buildSections());
	};

	that.hasPeriodPicker = function () {
		return true;
	};

	my.buildSections = function () {
		const sections = getResult(that.actions);
		return sections.map((section) => {
			if (!section.label) {
				return section;
			}

			if (section.condition !== undefined && !section.condition()) {
				return "";
			}

			const children = my.buildActions(section.children);

			if (!children.length) {
				return "";
			}

			return my.rightMenuSection(
				section.label,
				children.map((action) => action.content || action),
			);
		});
	};

	my.buildActions = function (data) {
		const actions = getResult(data);
		if (!Array.isArray(actions)) {
			return [];
		}

		return actions.filter(
			(action) => action.condition === undefined || action.condition(),
		);
	};

	/**
	 * Render the arguments of the function inside a
	 * `.right-menu-section` div.
	 *
	 * If the first argument is not a falsy value, it will be used
	 * as the label of the menu section.  A renderer is returned.
	 */
	my.rightMenuSection = function (...args) {
		const label = args.shift();

		return function (html) {
			html.div(
				{
					klass: "right-menu-section",
					"data-test": "right-menu-section",
				},
				(html) => {
					if (label) {
						// the label is not empty or null
						html.label(label);
					}
					html.render(args);
				},
			);
		};
	};

	/**
	 * Render the arguments of the function inside a right menu block that
	 * is folded by default.
	 *
	 * Folded blocks should be rendered within a "right menu section" (see
	 * `my.rightMenuSection`).  A link is displayed to expand/collapse the
	 * block.
	 */
	my.collapsedRightMenuBlock = function (...renderables) {
		return new CollapsedActionsBlockWidget({
			content: renderables,
		});
	};

	my.renderContentOn = function () {};
});
